import { useParams, Link } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import { courses as defaultCourses } from '../../mocks/courses';
import { getCourseById } from '../../api/apiServices';
import { useProgress } from '../../context/ProgressContext';
import { getScoreLabel, getScoreBg } from '../../types';
import Navbar from '../../components/feature/Navbar';
import { useAuth } from '../../context/useAuth';

export default function CertificatePage() {
  const { courseId } = useParams();
  const { user } = useAuth();
  const { getEnrollment, getQuizResult, enrollments } = useProgress();
  const printRef = useRef(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let found = defaultCourses.find(c => String(c.id) === String(courseId) || String(c._id) === String(courseId));
    if (found) {
      setCourse(found);
      setLoading(false);
    } else {
      getCourseById(courseId).then(res => {
        if (res?.success && res?.course) {
          setCourse(res.course);
        }
      }).catch(err => {
        console.error("Error loading certificate course:", err);
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [courseId]);

  const targetId = course?._id || course?.id || courseId;
  const enrollment = targetId ? getEnrollment(targetId) : undefined;
  const finalQuizId = course?.finalQuiz?._id || course?.finalQuiz?.id || 'final';
  const finalResult = targetId ? getQuizResult(finalQuizId) : undefined;

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!course || !enrollment?.courseCompleted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-4">
              <i className="ri-lock-line text-3xl text-amber-500" />
            </div>
            <p className="text-xl font-bold text-slate-800">Certificate Not Available Yet</p>
            <p className="text-slate-500 text-sm mt-2 mb-5">
              Complete all lessons and pass the final quiz for &quot;{course?.title || 'this course'}&quot; to unlock your official certificate.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/dashboard" className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 font-semibold rounded-xl hover:bg-slate-200">
                Dashboard
              </Link>
              <Link to={`/courses/${courseId}`} className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white font-semibold rounded-xl hover:bg-teal-700">
                Continue Course
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const completedDate = enrollment.completedAt
    ? new Date(enrollment.completedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const allQuizResults = enrollment.quizResults || [];
  const avgScore = allQuizResults.length
    ? Math.round(allQuizResults.reduce((s, r) => s + (r.score || 0), 0) / allQuizResults.length)
    : 85;
  const finalScore = finalResult?.score ?? (allQuizResults.find(r => r.isFinalQuiz)?.score ?? 90);
  const label = getScoreLabel(finalScore);

  const totalLessons = course.lessons?.length || (course.sections?.reduce((sum, s) => sum + (s.lectures?.length || 0), 0) || 1);

  return (
    <div className="min-h-screen bg-slate-100">
      <Navbar />
      <div className="pt-16">
        <div className="max-w-4xl mx-auto px-6 py-10">
          <div className="flex items-center justify-between mb-6 print:hidden">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Certificate of Completion</h1>
              <p className="text-slate-500 text-sm mt-0.5">Your official verified achievement</p>
            </div>
            <div className="flex gap-3">
              <Link to="/dashboard" className="px-4 py-2 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl cursor-pointer hover:bg-slate-50 whitespace-nowrap flex items-center gap-2">
                <i className="ri-arrow-left-line" /> Dashboard
              </Link>
              <button onClick={handlePrint} className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-teal-700 whitespace-nowrap flex items-center gap-2">
                <i className="ri-download-line" /> Print / Save PDF
              </button>
            </div>
          </div>

          <div ref={printRef} className="bg-white rounded-2xl overflow-hidden border-4 border-teal-600 shadow-xl">
            <div className="bg-teal-600 px-10 py-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white text-2xl font-bold">
                  <i className="ri-graduation-cap-fill" />
                </div>
                <div>
                  <span className="text-xl font-bold text-white tracking-wide">LearnHub</span>
                  <p className="text-xs text-teal-100">Academy of Excellence</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-teal-200 text-xs uppercase tracking-widest">Certificate of</p>
                <p className="text-white font-bold text-lg">Completion</p>
              </div>
            </div>

            <div className="px-10 py-10 text-center">
              <p className="text-slate-500 text-sm uppercase tracking-widest mb-3">This is to certify that</p>
              <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Georgia, serif' }}>
                {user?.name || user?.userName || 'Student'}
              </h2>
              <div className="w-24 h-0.5 bg-teal-400 mx-auto mb-5" />
              <p className="text-slate-600 text-base mb-2">has successfully completed all requirements for the course</p>
              <h3 className="text-2xl font-bold text-teal-700 mb-6">{course.title}</h3>

              <div className="flex items-center justify-center gap-8 mb-8">
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">{finalScore}%</p>
                  <p className="text-xs text-slate-400 mt-1">Final Score</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="text-center">
                  <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-bold border ${getScoreBg(label)}`}>{label}</span>
                  <p className="text-xs text-slate-400 mt-1">Performance</p>
                </div>
                <div className="w-px h-12 bg-slate-200" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-slate-900">{avgScore}%</p>
                  <p className="text-xs text-slate-400 mt-1">Average Score</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-8 bg-slate-50 rounded-xl p-4">
                <div className="text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg mx-auto mb-1.5">
                    <i className="ri-book-open-line text-teal-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-800">{totalLessons}</p>
                  <p className="text-xs text-slate-400">Lessons Completed</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg mx-auto mb-1.5">
                    <i className="ri-quiz-line text-amber-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-800">{Math.max(allQuizResults.length, 1)}</p>
                  <p className="text-xs text-slate-400">Quizzes Passed</p>
                </div>
                <div className="text-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg mx-auto mb-1.5">
                    <i className="ri-time-line text-emerald-600" />
                  </div>
                  <p className="text-sm font-bold text-slate-800">{course.duration || 'Self-paced'}</p>
                  <p className="text-xs text-slate-400">Course Duration</p>
                </div>
              </div>

              <div className="flex items-end justify-between pt-6 border-t border-slate-100">
                <div className="text-left">
                  <p className="font-bold text-slate-800 text-sm">{course.instructor || 'Lead Instructor'}</p>
                  <div className="w-28 h-0.5 bg-slate-300 mt-1 mb-1" />
                  <p className="text-xs text-slate-400">Course Instructor</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-2 border-2 border-teal-200">
                    <i className="ri-award-fill text-teal-600 text-3xl" />
                  </div>
                  <p className="text-xs text-slate-400">Verified Official Seal</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-slate-800 text-sm">{completedDate}</p>
                  <div className="w-28 h-0.5 bg-slate-300 mt-1 mb-1 ml-auto" />
                  <p className="text-xs text-slate-400">Date of Completion</p>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 px-10 py-4 flex items-center justify-between border-t border-slate-100">
              <p className="text-xs text-slate-400">
                Certificate ID: LH-{(courseId || 'CERT').slice(-8).toUpperCase()}-{(user?.id || 'USR').slice(-6).toUpperCase()}
              </p>
              <p className="text-xs text-slate-400">learnhub.academy · Verified Credential</p>
            </div>
          </div>

          {allQuizResults.length > 0 && (
            <div className="mt-6 bg-white rounded-xl border border-slate-100 p-5 print:hidden">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Performance Breakdown</h3>
              <div className="space-y-3">
                {allQuizResults.map((r, i) => {
                  const lbl = getScoreLabel(r.score);
                  return (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                      <span className="text-sm text-slate-600">{r.isFinalQuiz ? 'Final Exam' : `Quiz ${i + 1}`}</span>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${r.score >= 80 ? 'bg-emerald-500' : r.score >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${r.score}%` }} />
                        </div>
                        <span className="text-sm font-bold text-slate-800 w-10 text-right">{r.score}%</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getScoreBg(lbl)}`}>{lbl}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
